/**
 * World generation constants as typed data. The generator in `src/sim/worldgen.ts`
 * reads everything from here; tuning the world is a data edit.
 *
 * Vertical layout (GDD): the surface oscillates around y = 0, sea level is
 * y = -2, the build ceiling is y = +192 and the bedrock floor is y = -1024.
 * "Depth" means blocks below the surface plane: depth = max(0, -y).
 *
 * The M0 strata are four plain stone bands. The GDD's seven content strata
 * (own ores, hazards, dungeons) replace this table in later milestones — the
 * band mechanism stays.
 */

import { BLOCK, type BlockId } from "./blocks";

export const SEA_LEVEL = -2;
export const WORLD_CEILING = 192;
export const WORLD_FLOOR = -1024;

/**
 * Surface heightmap: two normalised fbm fields remapped to [-1, 1) and scaled,
 * so terrain stays inside ±(18 + 6) = ±24 around y = 0. Frequencies and octave
 * counts are the prototype's proven values.
 */
export const SURFACE = {
  continent: { frequency: 0.0031, octaves: 4, amplitude: 18, salt: 0 },
  hills: { frequency: 0.0135, octaves: 4, amplitude: 6, salt: 91 },
} as const;

/** Dirt/sand fill under the top block, and how high above sea level sand reaches. */
export const SURFACE_FILL = {
  depth: 4,
  /** Columns at or below this height get sand instead of grass/dirt. */
  beachMaxY: SEA_LEVEL + 1,
} as const;

export interface StratumBand {
  readonly name: string;
  /** Base stone of the band, before ores and caves. */
  readonly stone: BlockId;
  /** Band spans floorY <= y < ceilingY. */
  readonly floorY: number;
  readonly ceilingY: number;
}

/** Top to bottom. The first band catches everything above -30 up to the ceiling. */
export const STRATA: readonly StratumBand[] = [
  { name: "surface", stone: BLOCK.STONE, floorY: -30, ceilingY: WORLD_CEILING + 1 },
  { name: "limestone", stone: BLOCK.LIMESTONE, floorY: -120, ceilingY: -30 },
  { name: "shale", stone: BLOCK.SHALE, floorY: -300, ceilingY: -120 },
  { name: "deepstone", stone: BLOCK.DEEPSTONE, floorY: WORLD_FLOOR, ceilingY: -300 },
];

export interface OreVein {
  readonly block: BlockId;
  /** Ore appears only at depths in [minDepth, maxDepth] (depth = -y). */
  readonly minDepth: number;
  readonly maxDepth: number;
  /**
   * Vein granularity in blocks. Ore is rolled per vein-sized cell of the world
   * grid, so a winning cell becomes a cubical vein of about veinSize^3 blocks.
   */
  readonly veinSize: number;
  /** Probability per vein cell; equal to the fraction of band stone that is ore. */
  readonly frequency: number;
  /** Unique hash salt so ore types never correlate. */
  readonly salt: number;
}

/**
 * Frequencies ported from the prototype (coal 1.05% of stone, iron 0.75%).
 * Deep iron is the M0 reward for pushing past the shale boundary.
 */
export const ORES: readonly OreVein[] = [
  { block: BLOCK.COAL_ORE, minDepth: 4, maxDepth: 120, veinSize: 3, frequency: 0.01, salt: 1000 },
  { block: BLOCK.IRON_ORE, minDepth: 30, maxDepth: 200, veinSize: 3, frequency: 0.0075, salt: 2000 },
  { block: BLOCK.DEEP_IRON_ORE, minDepth: 180, maxDepth: 700, veinSize: 2, frequency: 0.005, salt: 3000 },
];

/**
 * Caves: two independent 3D fbm channels; a block is carved where both channels
 * pass near 0.5 at once, which is what produces crossing spaghetti tunnels
 * instead of isolated bubbles. Frequencies and threshold are the prototype's.
 */
export const CAVES = {
  frequency: 0.021,
  verticalFrequency: 0.03,
  octaves: 3,
  threshold: 0.055,
  saltA: 11,
  saltB: 23,
  /** Never carve within this many blocks of the bedrock floor. */
  bedrockGuard: 3,
} as const;

/** Trees: a deterministic per-column roll on grass above the beach band. */
export const TREES = {
  chance: 0.02,
  minTrunk: 3,
  maxTrunk: 5,
  leavesRadius: 2,
  saltChance: 404,
  saltTrunk: 405,
  saltLeaves: 406,
} as const;

/** Salt for the ragged bedrock layers just above the floor. */
export const BEDROCK_SALT = 9;
